import type { AllergiesDuJour, Reservation, ServiceResa, TableResa, ZoneResa } from '../../lib/reservationsApi';
import { STATUTS_AFFICHES } from './statuts';

/** Données de référence nécessaires pour afficher ou imprimer une cellule. */
export interface ContexteCellules { services: ServiceResa[]; zones: ZoneResa[]; tables: TableResa[]; allergies: AllergiesDuJour }

export const LIBELLE_SOURCE: Record<string, string> = { telephone: 'Téléphone', sur_place: 'Sur place', internet: 'Internet', widget: 'Widget', import: 'Import', autre: 'Autre' };

/** Noms des zones des tables d'une réservation (sans doublon). */
export const zonesDe = (r: Reservation, ctx: ContexteCellules): string[] => {
    const noms = r.tables.map((t) => ctx.zones.find((z) => z.id === ctx.tables.find((x) => x.id === t.id)?.zone_id)?.nom).filter((x): x is string => !!x);
    return [...new Set(noms)];
};

/** Texte brut d'une cellule : impression, tri, recherche. Les allergies n'y figurent que si elles ont été chargées (droit). */
export const valeurTexte = (id: string, r: Reservation, ctx: ContexteCellules): string => {
    switch (id) {
        case 'heure': return r.heure;
        case 'nom': return r.nom;
        case 'couverts': return String(r.couverts);
        case 'table': return r.tables.map((t) => t.nom).join(' + ');
        case 'zone': return zonesDe(r, ctx).join(', ');
        case 'statut': return STATUTS_AFFICHES[r.statut]?.label || r.statut;
        case 'source': return LIBELLE_SOURCE[r.source] || r.source;
        case 'telephone': return r.telephone || '';
        case 'email': return r.email || '';
        case 'allergies': { const a = ctx.allergies[r.id]; return a ? [a.allergenes.join(', '), a.note].filter(Boolean).join(' — ') : ''; }
        case 'etiquettes': return (r.etiquettes || []).map((e) => e.nom).join(', ');
        case 'occasion': return r.occasion || '';
        case 'commentaire_client': return r.commentaire_client || '';
        case 'notes_internes': return r.notes_internes || '';
        case 'service': return ctx.services.find((s) => s.id === r.service_id)?.nom || '';
        case 'pris_par': return r.pris_par || '';
        case 'duree': return r.fin.slice(11);
        default: return '';
    }
};

export interface Groupe { cle: string; titre: string; lignes: Reservation[] }

/** Tri puis regroupement selon la vue. Sans regroupement : un seul groupe sans titre. */
export const organiser = (liste: Reservation[], config: { regroupement: string; tri: { colonne: string; sens: string } | null }, ctx: ContexteCellules): Groupe[] => {
    const tri = config.tri || { colonne: 'heure', sens: 'asc' };
    const cle = (r: Reservation): string | number => (tri.colonne === 'nom' ? r.nom.toLowerCase() : tri.colonne === 'couverts' ? r.couverts : tri.colonne === 'statut' ? valeurTexte('statut', r, ctx) : r.debut);
    const sens = tri.sens === 'desc' ? -1 : 1;
    const triees = [...liste].sort((a, b) => { const x = cle(a); const y = cle(b); return (x < y ? -1 : x > y ? 1 : a.debut.localeCompare(b.debut)) * sens; });
    if (config.regroupement === 'aucun') return [{ cle: 'tout', titre: '', lignes: triees }];
    const m = new Map<string, Reservation[]>();
    for (const r of triees) {
        const k = config.regroupement === 'creneau' ? `${r.heure.slice(0, 2)}:${String(Math.floor(Number(r.heure.slice(3, 5)) / 15) * 15).padStart(2, '0')}` : (zonesDe(r, ctx)[0] || 'Sans table');
        m.set(k, [...(m.get(k) || []), r]);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([k, lignes]) => ({ cle: k, titre: k, lignes }));
};
